/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
/**
 * @fileoverview Home Calendar logic module.
 * Handles calendar data initialization, date parsing/formatting utilities,
 * and event spanning calculations for the home page calendar widget.
 * @module logic/homeCalendar
 */

import {
  CONTENT_TYPE_JSON,
  TOKEN_EXPIRED_MESSAGE,
} from "../../utils/apiConstants.js";
import { refreshToken, signOut } from "../../utils/common.js";

const DATE_PAD_WIDTH = 2;
const PAD_CHAR = "0";
const MINUTES_PER_HOUR = 60;
const DECIMAL_RADIX = 10;
const HOURS_MATCH_INDEX = 1;
const MINUTES_MATCH_INDEX = 2;

/**
 * Initializes the home calendar by fetching registration, business, B2B, breakfast,
 * virtual events and appointments for the current month and next month.
 *
 * @param {string} userId - The unique identifier of the user requesting the calendar data
 * @param {Function} navigate - Navigation function for redirecting on auth errors
 * @returns {Promise<Object>} Calendar payload arrays for HomeCalendar rendering
 */
export const initializeHomeCalendar = async (userId, navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}homeCalendar/user/${userId}`,
      options,
    );
    const data = await response.json();

    if (!response.ok && data?.message !== TOKEN_EXPIRED_MESSAGE) {
      logger.error("Home calendar request failed:", {
        status: response.status,
        message: data?.message,
      });
      return {
        events: [],
        businessEvents: [],
        b2bEvents: [],
        breakfastEvents: [],
        virtualEvents: [],
        appointments: [],
        virtualAppointments: [],
      };
    }

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        if (navigate) {
          navigate("/");
        }
        return {
          events: [],
          businessEvents: [],
          b2bEvents: [],
          breakfastEvents: [],
          virtualEvents: [],
          appointments: [],
          virtualAppointments: [],
        };
      }
      return await initializeHomeCalendar(userId, navigate);
    }

    // Ensure we always return the expected structure
    return {
      events: Array.isArray(data.events) ? data.events : [],
      businessEvents: Array.isArray(data.businessEvents)
        ? data.businessEvents
        : [],
      b2bEvents: Array.isArray(data.b2bEvents) ? data.b2bEvents : [],
      breakfastEvents: Array.isArray(data.breakfastEvents)
        ? data.breakfastEvents
        : [],
      virtualEvents: Array.isArray(data.virtualEvents)
        ? data.virtualEvents
        : [],
      appointments: Array.isArray(data.appointments) ? data.appointments : [],
      virtualAppointments: Array.isArray(data.virtualAppointments)
        ? data.virtualAppointments
        : [],
    };
  } catch (error) {
    logger.error("Error initializing home calendar:", {
      err: sanitizeError(error),
    });
    return {
      events: [],
      businessEvents: [],
      b2bEvents: [],
      breakfastEvents: [],
      virtualEvents: [],
      appointments: [],
      virtualAppointments: [],
    };
  }
};

/**
 * Parse date string in local timezone
 * @param {string|Date} dateString - Date string or Date object
 * @returns {Date|null} Parsed date in local timezone or null
 */
export const parseLocalDate = (dateString) => {
  if (!dateString) {
    return null;
  }

  // If it's already a Date object, return it
  if (dateString instanceof Date) {
    return dateString;
  }

  // If it's a string in YYYY-MM-DD format, append T00:00:00 to parse in local time
  if (typeof dateString === "string") {
    // Check if it's already an ISO string with time
    if (dateString.includes("T") || dateString.includes(" ")) {
      return new Date(dateString);
    }
    // If it's just YYYY-MM-DD, append T00:00:00 to parse in local timezone
    return new Date(dateString + "T00:00:00");
  }

  return new Date(dateString);
};

/**
 * Normalize date to start of day for comparison
 * @param {string|Date} date - Date string or Date object
 * @returns {Date} Normalized date at start of day
 */
export const normalizeDate = (date) => {
  const parsed = parseLocalDate(date);
  if (!parsed || isNaN(parsed.getTime())) {
    return new Date(); // Fallback to today
  }
  const normalized = new Date(parsed);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

/**
 * Check if two dates are the same day
 * @param {Date} date1 - First date
 * @param {Date} date2 - Second date
 * @returns {boolean} True if dates are the same day
 */
export const isSameDay = (date1, date2) => {
  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  );
};

/**
 * Format date as YYYY-MM-DD
 * @param {Date} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(DATE_PAD_WIDTH, PAD_CHAR);
  const day = String(date.getDate()).padStart(DATE_PAD_WIDTH, PAD_CHAR);
  return `${year}-${month}-${day}`;
};

/**
 * Parse time string to minutes for sorting
 * @param {string} timeStr - Time string in various formats
 * @returns {number} Minutes since midnight, or Infinity if invalid
 */
export const parseTimeToMinutes = (timeStr) => {
  if (!timeStr) {
    return Infinity;
  } // Put items without time at the end

  // Handle HH:MM format
  const timeMatch = timeStr.match(/^(\d{1,2}):(\d{2})/);
  if (timeMatch) {
    const hours = parseInt(timeMatch[HOURS_MATCH_INDEX], DECIMAL_RADIX);
    const minutes = parseInt(timeMatch[MINUTES_MATCH_INDEX], DECIMAL_RADIX);
    return hours * MINUTES_PER_HOUR + minutes;
  }

  // Try to parse as Date if it's an ISO string
  try {
    const date = new Date(timeStr);
    if (!isNaN(date.getTime())) {
      return date.getHours() * MINUTES_PER_HOUR + date.getMinutes();
    }
  } catch (e) {
    // Ignore
  }

  return Infinity;
};

/**
 * Format time string to 12-hour format
 * @param {string} timeStr - Time string in various formats
 * @param {string} language - Language code for localization
 * @returns {Object} Object with displayTime and timeMinutes
 */
export const formatTime = (timeStr, language = "en-US") => {
  let displayTime = "";
  let timeMinutes = Infinity;

  if (timeStr) {
    try {
      // Handle different time formats
      let timeDate;
      // Check if it's already in HH:MM format (24-hour)
      const timeMatch = timeStr.match(/^(\d{1,2}):(\d{2})/);
      if (timeMatch) {
        const hours = parseInt(timeMatch[HOURS_MATCH_INDEX], DECIMAL_RADIX);
        const minutes = parseInt(timeMatch[MINUTES_MATCH_INDEX], DECIMAL_RADIX);
        // Create a date object with today's date and the time
        timeDate = new Date();
        timeDate.setHours(hours, minutes, 0, 0);
      } else {
        // Try parsing as ISO string
        timeDate = new Date(timeStr);
      }

      if (!isNaN(timeDate.getTime())) {
        displayTime = timeDate.toLocaleTimeString(language, {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
        timeMinutes = timeDate.getHours() * MINUTES_PER_HOUR + timeDate.getMinutes();
      } else {
        displayTime = timeStr;
        timeMinutes = parseTimeToMinutes(timeStr);
      }
    } catch (e) {
      displayTime = timeStr;
      timeMinutes = parseTimeToMinutes(timeStr);
    }
  }

  return { displayTime, timeMinutes };
};

/**
 * Whether a calendar event's end date is before today (local calendar day).
 * An event that ends today stays active.
 * @param {string|Date} endDate - Event end / occurrence date
 * @param {Date} [reference=new Date()]
 * @returns {boolean}
 */
export const isHomeCalendarEventPast = (endDate, reference = new Date()) => {
  const eventEnd = normalizeDate(endDate);
  const today = normalizeDate(reference);
  return eventEnd < today;
};

/**
 * Get all calendar-bar events (registration, business, B2B, breakfast, virtual)
 * with their date ranges for spanning rendering.
 * @param {Object} calendarData - Calendar data object
 * @returns {Array} Array of multi-day / single-day calendar events
 */
export const getMultiDayEvents = (calendarData) => {
  const multiDayEvents = [];

  const pushSingleDay = (items, type, timeField = "hora") => {
    (items || []).forEach((event) => {
      const eventDate = normalizeDate(event.fechaEvento);
      multiDayEvents.push({
        type,
        id: event._id,
        name: event.nombre,
        time: event[timeField] || event.hora || event.horaInicio || "",
        startDate: eventDate,
        endDate: eventDate,
        isPast: isHomeCalendarEventPast(eventDate),
      });
    });
  };

  const pushRange = (items, type) => {
    (items || []).forEach((event) => {
      const startDate = normalizeDate(event.fechaInicioEvento);
      const endDate = normalizeDate(event.fechaFinEvento);
      multiDayEvents.push({
        type,
        id: event._id,
        name: event.nombre,
        time: event.horaInicio || "",
        startDate,
        endDate,
        isPast: isHomeCalendarEventPast(endDate),
      });
    });
  };

  pushSingleDay(calendarData.events, "event", "hora");
  pushRange(calendarData.businessEvents, "businessEvent");
  pushRange(calendarData.b2bEvents, "b2bEvent");
  pushSingleDay(calendarData.breakfastEvents, "breakfastEvent", "horaInicio");
  pushSingleDay(calendarData.virtualEvents, "virtualEvent", "hora");

  return multiDayEvents;
};

/**
 * Get single-day events (appointments and virtual appointments) for a specific date
 * @param {Date} date - Date to get events for
 * @param {Object} calendarData - Calendar data object
 * @param {Function} t - Translation function
 * @param {string} language - Language code
 * @returns {Array} Array of events for the date, sorted by time
 */
export const getSingleDayEventsForDate = (
  date,
  calendarData,
  t,
  language = "en-US",
) => {
  const eventsForDate = [];
  const normalizedCheckDate = normalizeDate(date);

  // Check regular appointments
  calendarData.appointments.forEach((appointment) => {
    const appointmentDate = normalizeDate(appointment.fecha);

    if (isSameDay(appointmentDate, normalizedCheckDate)) {
      const { displayTime, timeMinutes } = formatTime(
        appointment.horaInicio || "",
        language,
      );
      const { displayTime: displayEndTime } = formatTime(
        appointment.horaFin || "",
        language,
      );

      eventsForDate.push({
        type: "appointment",
        id: appointment._id,
        name:
          appointment.nombreEmpresaCompradora ||
          t("pages.viewAppointments.appointment"),
        buyerName: appointment.nombreUsuarioComprador || "",
        time: displayTime,
        endTime: displayEndTime,
        timeMinutes,
      });
    }
  });

  // Check virtual appointments
  calendarData.virtualAppointments.forEach((virtualAppointment) => {
    const virtualDate = normalizeDate(virtualAppointment.diaCitaVirtual);

    if (isSameDay(virtualDate, normalizedCheckDate)) {
      const { displayTime, timeMinutes } = formatTime(
        virtualAppointment.horaInicio ||
          virtualAppointment.horaInicioCitaVirtual,
        language,
      );
      const { displayTime: displayEndTime } = formatTime(
        virtualAppointment.horaFin || "",
        language,
      );

      eventsForDate.push({
        type: "virtualAppointment",
        id: virtualAppointment._id,
        name:
          virtualAppointment.nombreEmpresaCompradora ||
          t("pages.viewAppointments.virtualAppointment"),
        buyerName: virtualAppointment.nombreUsuarioComprador || "",
        time: displayTime,
        endTime: displayEndTime,
        timeMinutes,
      });
    }
  });

  // Sort a copy by time (earliest first) so the source list is left unchanged.
  return [...eventsForDate].sort((a, b) => a.timeMinutes - b.timeMinutes);
};

/**
 * Get every item happening on a date: events and business events covering it,
 * followed by that day's appointments, all ordered by start time.
 * @param {Date} date - Date to build the agenda for
 * @param {Object} calendarData - Calendar data object
 * @param {Function} t - Translation function
 * @param {string} language - Language code
 * @returns {{items: Array, appointmentCount: number}} Agenda items and the appointment total
 */
export const getDayAgendaForDate = (
  date,
  calendarData,
  t,
  language = "en-US",
) => {
  const normalizedCheckDate = normalizeDate(date);
  const items = [];

  getMultiDayEvents(calendarData).forEach((event) => {
    const eventStart = normalizeDate(event.startDate);
    const eventEnd = normalizeDate(event.endDate);

    if (normalizedCheckDate >= eventStart && normalizedCheckDate <= eventEnd) {
      const { displayTime, timeMinutes } = formatTime(event.time, language);

      items.push({
        type: event.type,
        id: event.id,
        name: event.name,
        buyerName: "",
        time: displayTime,
        endTime: "",
        timeMinutes,
        isPast: Boolean(event.isPast),
      });
    }
  });

  const appointments = getSingleDayEventsForDate(
    date,
    calendarData,
    t,
    language,
  );
  items.push(...appointments);

  return {
    items: [...items].sort((a, b) => a.timeMinutes - b.timeMinutes),
    appointmentCount: appointments.length,
  };
};

/**
 * Format a date as "Weekday / DD / month, YYYY" in the given language
 * @param {Date} date - Date to format
 * @param {string} language - Language code
 * @returns {string} Formatted heading, or empty string for an invalid date
 */
export const formatDayHeading = (date, language = "en-US") => {
  const parsed = parseLocalDate(date);
  if (!parsed || isNaN(parsed.getTime())) {
    return "";
  }

  const capitalize = (value) => value.charAt(0).toUpperCase() + value.slice(1);
  const weekday = capitalize(
    parsed.toLocaleDateString(language, { weekday: "long" }),
  );
  const month = capitalize(
    parsed.toLocaleDateString(language, { month: "long" }),
  );
  const day = String(parsed.getDate()).padStart(DATE_PAD_WIDTH, PAD_CHAR);

  return `${weekday} / ${day} / ${month}, ${parsed.getFullYear()}`;
};

/**
 * Get spanning events for a specific date
 * @param {Date} date - Date to get spanning events for
 * @param {Object} calendarData - Calendar data object
 * @returns {Array} Array of spanning events for the date
 */
export const getSpanningEventsForDate = (date, calendarData) => {
  const normalizedCheckDate = normalizeDate(date);
  const spanningEvents = [];
  const multiDayEvents = getMultiDayEvents(calendarData);

  multiDayEvents.forEach((event) => {
    const eventStart = normalizeDate(event.startDate);
    const eventEnd = normalizeDate(event.endDate);

    if (normalizedCheckDate >= eventStart && normalizedCheckDate <= eventEnd) {
      spanningEvents.push({
        type: event.type,
        id: event.id,
        name: event.name,
        isPast: Boolean(event.isPast),
      });
    }
  });

  return spanningEvents;
};

/**
 * Calculate spanning events for a week row with row assignment to prevent overlaps
 * @param {Array} week - Array of day objects for the week
 * @param {Object} calendarData - Calendar data object
 * @returns {Array} Array of spanning events with row assignments
 */
export const getSpanningEventsForWeek = (week, calendarData) => {
  const spanningEvents = [];
  const multiDayEvents = getMultiDayEvents(calendarData);

  multiDayEvents.forEach((event) => {
    const eventStart = normalizeDate(event.startDate);
    const eventEnd = normalizeDate(event.endDate);

    // Find which days in this week the event spans
    const daysInWeek = [];
    week.forEach((day, dayIndex) => {
      const dayDate = normalizeDate(day.date);
      if (dayDate >= eventStart && dayDate <= eventEnd) {
        daysInWeek.push({ dayIndex, day });
      }
    });

    if (daysInWeek.length > 0) {
      // Group consecutive days
      const groups = [];
      let currentGroup = [daysInWeek[0]];

      for (let i = 1; i < daysInWeek.length; i++) {
        const prevDayIndex = daysInWeek[i - 1].dayIndex;
        const currDayIndex = daysInWeek[i].dayIndex;

        if (currDayIndex === prevDayIndex + 1) {
          // Consecutive, add to current group
          currentGroup.push(daysInWeek[i]);
        } else {
          // Not consecutive, start new group
          groups.push(currentGroup);
          currentGroup = [daysInWeek[i]];
        }
      }
      groups.push(currentGroup);

      // Create spanning event for each consecutive group
      groups.forEach((group) => {
        spanningEvents.push({
          ...event,
          startDayIndex: group[0].dayIndex,
          endDayIndex: group[group.length - 1].dayIndex,
          span: group.length,
          row: 0, // Will be assigned later
        });
      });
    }
  });

  // Assign rows to prevent overlaps
  const rows = [];
  spanningEvents.forEach((event) => {
    let assignedRow = -1;

    // Find the first row where this event doesn't overlap
    for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
      const rowEvents = rows[rowIndex];
      const overlaps = rowEvents.some((existingEvent) => {
        // Check if events overlap
        return !(
          event.endDayIndex < existingEvent.startDayIndex ||
          event.startDayIndex > existingEvent.endDayIndex
        );
      });

      if (!overlaps) {
        assignedRow = rowIndex;
        break;
      }
    }

    // If no row found, create a new one
    if (assignedRow === -1) {
      assignedRow = rows.length;
      rows.push([]);
    }

    event.row = assignedRow;
    rows[assignedRow].push(event);
  });

  return spanningEvents;
};

/**
 * Get the maximum row number for spanning events on a specific date in a week
 * @param {Date} date - Date to check
 * @param {Array} week - Array of day objects for the week
 * @param {Object} calendarData - Calendar data object
 * @returns {number} Maximum row number, or -1 if no spanning events
 */
export const getMaxSpanningEventRowForDate = (date, week, calendarData) => {
  const spanningEvents = getSpanningEventsForWeek(week, calendarData);
  const normalizedCheckDate = normalizeDate(date);
  let maxRow = -1;

  spanningEvents.forEach((spanningEvent) => {
    const eventStart = normalizeDate(spanningEvent.startDate);
    const eventEnd = normalizeDate(spanningEvent.endDate);

    // Check if this spanning event covers this date
    if (
      normalizedCheckDate >= eventStart &&
      normalizedCheckDate <= eventEnd &&
      spanningEvent.row > maxRow
    ) {
      maxRow = spanningEvent.row;
    }
  });

  return maxRow;
};
